import React, { useState } from 'react'
import { Cpu, Play, ChevronDown, ChevronRight } from 'lucide-react'

const MCP_TOOLS = [
  {
    name: 'analyze_student_answer',
    description: '학생 답안을 분석하여 오개념 감지, 피드백, 다음 단계를 반환합니다.',
    params: {
      student_id: { type: 'string', required: true, example: 'student_001' },
      step_id: { type: 'string', required: true, example: 'LS05' },
      answer_text: { type: 'string', required: true, example: '기울기는 5, y절편은 2입니다.' },
      score: { type: 'number', required: true, example: 20 },
    },
    example_result: {
      misconceptions: ['M03: y절편과 기울기 혼동'],
      feedback: 'y = ax+b에서 a가 기울기, b가 y절편입니다.',
      next_step: 'LS05 재학습',
      score: 20,
    },
  },
  {
    name: 'get_step_explanation',
    description: '특정 학습 단계의 개념 설명, 예시, 연습 문제를 반환합니다.',
    params: {
      step_id: { type: 'string', required: true, example: 'LS05' },
    },
    example_result: {
      title: '기울기 개념',
      explanation: 'x가 1 증가할 때 y의 변화량',
      example: { question: 'y=2x+1의 기울기?', answer: '2' },
    },
  },
  {
    name: 'evaluate_linear_function',
    description: '기울기와 y절편을 받아 함수 분류, 그래프 방향, 키포인트를 반환합니다.',
    params: {
      slope_value: { type: 'number', required: true, example: 2 },
      intercept_value: { type: 'number', required: true, example: 1 },
    },
    example_result: {
      equation: 'y = 2x + 1',
      type: '일반 1차 함수',
      direction: '증가 (우상향)',
      y_intercept: 1,
      x_intercept: -0.5,
    },
  },
  {
    name: 'get_misconception_feedback',
    description: '오개념 ID를 받아 맞춤형 피드백과 복습 단계를 반환합니다.',
    params: {
      misconception_id: { type: 'string', required: true, example: 'M03', enum: ['M01','M02','M03','M04','M05','M06','M07','M08'] },
    },
    example_result: {
      id: 'M03',
      title: 'y절편과 기울기는 달라요!',
      message: 'y=ax+b에서 a는 기울기, b는 y절편입니다.',
      remedial_step: 'LS05',
    },
  },
  {
    name: 'recommend_next_step',
    description: '학생 ID와 현재 단계를 받아 다음 학습 단계를 추천합니다.',
    params: {
      student_id: { type: 'string', required: true, example: 'student_001' },
      current_step: { type: 'string', required: true, example: 'LS05' },
      score: { type: 'number', required: true, example: 85 },
    },
    example_result: {
      recommendation: 'advance',
      next_step: 'LS06',
      reason: '기울기 마스터! y절편 학습으로 진행',
    },
  },
  {
    name: 'get_ontology_class',
    description: 'OWL 클래스 이름으로 정의, 속성, 관련 규칙을 조회합니다.',
    params: {
      class_name: { type: 'string', required: true, example: 'LinearFunction' },
    },
    example_result: {
      uri: 'math:LinearFunction',
      label_ko: '1차 함수',
      comment: '일반형 y=ax+b인 함수',
      properties: ['hasSlope', 'hasYIntercept', 'hasEquation'],
    },
  },
  {
    name: 'generate_practice_problem',
    description: '특정 단계와 난이도에 맞는 연습 문제를 생성합니다.',
    params: {
      step_id: { type: 'string', required: true, example: 'LS05' },
      difficulty: { type: 'string', required: false, example: 'medium', enum: ['easy','medium','hard'] },
    },
    example_result: {
      question: 'y = 3x - 2에서 기울기와 y절편을 구하시오.',
      answer: '기울기: 3, y절편: -2',
      hint: 'y = ax + b에서 a가 기울기입니다.',
    },
  },
]

export default function MCPPage() {
  const [selectedTool, setSelectedTool] = useState(MCP_TOOLS[0])
  const [paramValues, setParamValues] = useState<Record<string, string | number>>({})
  const [result, setResult] = useState<unknown>(null)
  const [loading, setLoading] = useState(false)
  const [showJson, setShowJson] = useState(false)

  const handleInvoke = async () => {
    setLoading(true)
    await new Promise(r => setTimeout(r, 600))
    setResult(selectedTool.example_result)
    setLoading(false)
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Cpu size={22} className="text-indigo-600" />
        <div>
          <h1 className="text-xl font-bold text-slate-800">MCP 콘솔</h1>
          <p className="text-sm text-slate-500">Model Context Protocol — Agentic AI 도구 인터페이스</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tool selector */}
        <div className="space-y-4">
          <div className="card">
            <p className="text-sm font-semibold text-slate-700 mb-3">MCP 도구 선택 ({MCP_TOOLS.length}개)</p>
            <div className="space-y-1.5">
              {MCP_TOOLS.map(tool => (
                <button
                  key={tool.name}
                  onClick={() => { setSelectedTool(tool); setResult(null); setParamValues({}) }}
                  className={`w-full text-left px-3 py-2.5 rounded-xl transition-all ${selectedTool.name === tool.name ? 'bg-indigo-100 text-indigo-700 font-medium' : 'hover:bg-slate-50 text-slate-600'}`}
                >
                  <p className="text-sm font-mono font-semibold">{tool.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{tool.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* MCP Schema */}
          <div className="card">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-slate-700">도구 스키마</p>
              <button onClick={() => setShowJson(!showJson)} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
                JSON {showJson ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              </button>
            </div>
            {showJson ? (
              <pre className="text-xs bg-slate-800 text-emerald-400 rounded-lg p-3 overflow-auto max-h-48 font-mono">
                {JSON.stringify({ name: selectedTool.name, parameters: selectedTool.params }, null, 2)}
              </pre>
            ) : (
              <div className="space-y-2">
                {Object.entries(selectedTool.params).map(([key, def]) => (
                  <div key={key} className="flex items-center gap-2 text-xs">
                    <span className={`font-mono font-bold ${def.required ? 'text-red-500' : 'text-slate-400'}`}>{key}</span>
                    <span className="text-slate-400">{def.type}</span>
                    {def.required && <span className="text-red-400">필수</span>}
                    {'enum' in def && Array.isArray((def as any).enum) && (
                      <span className="text-slate-400">[{(def as any).enum.join('|')}]</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Invoke panel */}
        <div className="space-y-4">
          <div className="card">
            <p className="text-sm font-semibold text-slate-700 mb-3">
              <span className="font-mono text-indigo-600">{selectedTool.name}</span> 실행
            </p>
            <p className="text-xs text-slate-500 mb-4">{selectedTool.description}</p>

            <div className="space-y-3">
              {Object.entries(selectedTool.params).map(([key, def]) => (
                <div key={key}>
                  <label className="text-xs font-semibold text-slate-600 flex items-center gap-1">
                    {key} <span className="text-xs text-slate-400">({def.type})</span>
                    {def.required && <span className="text-red-400 text-xs">*</span>}
                  </label>
                  {'enum' in def && Array.isArray((def as any).enum) ? (
                    <select
                      className="w-full mt-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                      defaultValue={String(def.example)}
                    >
                      {((def as any).enum as string[]).map((v: string) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      defaultValue={String(def.example)}
                      className="w-full mt-1 text-sm font-mono border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                      placeholder={String(def.example)}
                    />
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={handleInvoke}
              disabled={loading}
              className="btn-primary w-full mt-4 flex items-center justify-center gap-2"
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> 실행 중...</>
              ) : (
                <><Play size={15} /> 도구 실행 (데모)</>
              )}
            </button>
          </div>

          {/* Result */}
          {result && (
            <div className="card">
              <p className="text-sm font-semibold text-slate-700 mb-2">실행 결과</p>
              <pre className="text-xs bg-slate-800 text-emerald-400 rounded-xl p-4 overflow-auto max-h-72 font-mono leading-relaxed">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}

          {/* MCP Protocol info */}
          <div className="card bg-indigo-50 border border-indigo-200">
            <p className="text-xs font-semibold text-indigo-700 mb-2">MCP 프로토콜 정보</p>
            <div className="space-y-1 text-xs text-indigo-700">
              <div>엔드포인트: <code className="bg-indigo-100 px-1 rounded">/api/v1/mcp/invoke</code></div>
              <div>메서드: <code className="bg-indigo-100 px-1 rounded">POST</code></div>
              <div>형식: <code className="bg-indigo-100 px-1 rounded">{'{ "tool": "name", "parameters": {...} }'}</code></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
